/**
 * shadcn/ui Checkbox — vendored + tokenized for CertAIn v2 (Phase 1 fan-out foundation).
 *
 * Radix Checkbox. Checked state uses the CertAIn brand `accent-primary` fill
 * with on-brand indicator; hairline border via `border-border-strong`. Check
 * glyph is an inline SVG (stroke=currentColor) — no lucide, no raw color.
 * Guard-clean, resolves inside `[data-theme="v2"]`.
 *
 * Used by Settings toggles/filter rows and table row selection (DataTableV2 can
 * lead a row with one).
 */
import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@/lib/utils";

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-[4px] border border-border-strong bg-surface-elevated shadow-sm transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-canvas",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:border-transparent data-[state=checked]:bg-accent-primary data-[state=checked]:text-text-on-brand",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        width={12}
        height={12}
        aria-hidden
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
